import { useState, useRef, ReactNode, useCallback, Fragment } from 'react';

import { Box, PopperProps } from '@mui/material';
import clsx from 'clsx';
import { useCombobox } from 'downshift';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';

import type { AutocompleteProps } from './types';
import * as Icons from '../../../icons';
import Scroller from '../../../scroller/Scroller';
import { VirtuosoScroller } from '../../../scroller/VirtuosoScroller';
import { dropdownClassNames } from '../classNames';
import { NoItemsFound } from '../components/NoItemsFound';
import { useCustomItemAddition } from '../hooks/useCustomItemAddition';
import { EnhancedItem, useEnhancedItems } from '../hooks/useEnhancedItems';
import { useFilteredItems } from '../hooks/useFilteredItems';
import { useItemStringConverters } from '../hooks/useItemStringConverters';
import { useKeyboardNavigationScrolling } from '../hooks/useKeyboardNavigationScrolling';
import {
  MainContainer,
  Label,
  ToggleButton,
  CaretContainer,
  HintMessage,
  PrefixContainer,
  PlaceholderContainer,
  SearchInputContainer,
  SearchInput,
  MenuList,
  CustomItemHint,
  DropdownContainer,
  StyledPopper as Popper,
  PrefixOutside,
  SuffixOutside,
  SelectedItemsContainer,
  autocompleteSizeToFormLabelSize,
} from '../SharedStyledComponents';
import { getItemByValue } from '../utils/itemLookup';
import { renderMenuItemByType } from '../utils/renderMenuItems';

const SAME_WIDTH_MODIFIER_NAME = 'sameWidth';

const popperModifiers: PopperProps['modifiers'] = [
  {
    name: 'offset',
    options: {
      offset: [0, 4],
    },
  },
  {
    name: 'preventOverflow',
    options: {
      boundary: 'clippingParents',
    },
  },
  {
    name: SAME_WIDTH_MODIFIER_NAME,
    enabled: true,
    phase: 'beforeWrite',
    requires: ['computeStyles'],
    fn: ({ state }) => {
      const refWidth = state.rects.reference.width;
      state.styles.popper.width = `${refWidth}px`;
    },
    effect: ({ state }) => {
      const refWidth = (state.elements.reference as HTMLElement).offsetWidth;
      state.elements.popper.style.width = `${refWidth}px`;
    },
  },
];

/**
 * Autocomplete component for single item selection.
 * Allows users to select a single item from a dropdown list with search functionality.
 */
export const Autocomplete = <Item,>({
  label,
  className,
  placeholder = 'Select an item...',
  searchInputPlaceholder = 'Search...',
  prefix,
  prefixOutside,
  suffixOutside,
  hint,
  error,
  success = false,
  items: propItems,
  filterItems: customFilterItems,
  renderMenuItem: customRenderMenuItem,
  renderSelectedItem: customRenderSelectedItem,
  renderMenuItemGroupLabel: customRenderMenuItemGroupLabel,
  value,
  isItemDisabled,
  disabled = false,
  readOnly = false,
  renderNoItemsFound: customRenderNoItemsFound,
  addCustomItem,
  size = 'medium',
  testId = 'autocomplete-root',
  addCustomItemHint = 'Tap enter key to add custom item!',
  getStringValue,
  getLabel,
  // events
  onOpen,
  onChange,
  onToggleBlur,
  onToggleFocus,
  onSearchInputBlur,
  onSearchInputFocus,
  onToggleMouseOver,
  onToggleMouseLeave,
  popoverSize,
  useVirtualization = false,
  groupBy,
  sx,
  renderToggle,
}: AutocompleteProps<Item>) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement | VirtuosoHandle | null>(null);
  const popoverMaxHeight: number | string = popoverSize?.maxHeight || 250;
  const [virtuosoHeight, setVirtuosoHeight] = useState(popoverMaxHeight);

  const [searchInputValue, setSearchInputValue] = useState('');

  const { itemToStringLabel, itemToStringValue } = useItemStringConverters({
    getLabel,
    getStringValue,
  });

  const handleCustomItemAdded = useCallback(
    (newItem: Item) => {
      // Call onChange directly with the newItem instead of using setValue
      // to avoid race condition where items array hasn't updated yet
      onChange?.({
        value: itemToStringValue(newItem),
        selectedItem: newItem,
      });
      setSearchInputValue('');
    },
    [itemToStringValue, onChange]
  );

  const { items, isItemUniqueValue, handleAddCustomItem } =
    useCustomItemAddition({
      searchInputValue,
      addCustomItem,
      propItems,
      itemToStringValue,
      onItemAdded: handleCustomItemAdded,
    });

  const filteredItems = useFilteredItems({
    items,
    searchInputValue,
    itemToStringLabel,
    customFilterItems,
  });

  const enhancedFilteredItems = useEnhancedItems({
    items: filteredItems,
    value,
    showSelectAllItem: false, // Autocomplete doesn't support select all
    groupBy,
    itemToStringValue,
    itemToStringLabel,
    isItemDisabled,
    isGroupDisabled: undefined, // Autocomplete doesn't support group disabling
  });

  const getItemByValueMemoized = useCallback(
    (value: string | null | undefined): Item | null | undefined => {
      return getItemByValue(value, items, itemToStringValue);
    },
    [items, itemToStringValue]
  );

  const selectedItem = getItemByValueMemoized(value);

  const setValue = (value: string | null) => {
    const selectedItem = getItemByValueMemoized(value);
    onChange?.({
      value,
      selectedItem: selectedItem === undefined ? null : selectedItem,
    });
  };

  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getItemProps,
    getInputProps,
    highlightedIndex,
  } = useCombobox<EnhancedItem<Item>>({
    selectedItem: null, // intentionally not using hook state, we control our own
    inputValue: searchInputValue, // controlled
    items: enhancedFilteredItems,

    onInputValueChange({ inputValue }) {
      setSearchInputValue(inputValue ?? '');
    },

    onIsOpenChange({ isOpen }) {
      // each time the popper is opened, we want to reset the search input
      setSearchInputValue('');
      onOpen?.();

      if (!isOpen) {
        // because we are using the combobox, we need to manually call the onBlur event
        // onBlur does not fire automatically when component no longer exists.
        // therefore, we do this manually. You might say we simulate the blur.
        onSearchInputBlur?.();
      }

      // when the popper is closed, we want to focus on the toggle button
      if (!isOpen) {
        toggleButtonRef.current?.focus();
      }
    },

    onSelectedItemChange({ selectedItem }) {
      if (selectedItem === null) {
        return;
      }

      // Only handle regular items (not group headers)
      if (selectedItem.itemType === 'regular') {
        setValue(itemToStringValue(selectedItem.item));
      }
    },

    itemToString: (item) => item?.value ?? '',
    isItemDisabled: (item) => item.itemType === 'groupLabel' || item.isDisabled,
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      switch (type) {
        // fixes behaviour bug, after selection, the first character you type in to search field was ignored.
        case useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem:
          return {
            ...changes,
            inputValue: state.inputValue,
          };
        case useCombobox.stateChangeTypes.InputClick:
          return {
            ...changes,
            isOpen: true,
          };
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: false, // close menu after selection for single-select
            inputValue: '', // clear search input
          };
        default:
          return changes;
      }
    },
  });

  // Handle keyboard navigation scrolling for highlighted items
  const { handleItemMouseMove } = useKeyboardNavigationScrolling(
    isOpen,
    highlightedIndex,
    {
      isVirtualized: useVirtualization,
      menuRef,
    }
  );

  // Wrap getItemProps to inject mouse move handler
  const getItemPropsWithMouseHandler = useCallback(
    (options: Parameters<typeof getItemProps>[0]) => {
      const itemProps = getItemProps(options);
      return {
        ...itemProps,
        onMouseMove: (e: React.MouseEvent) => {
          handleItemMouseMove();
          itemProps.onMouseMove?.(e);
        },
      };
    },
    [getItemProps, handleItemMouseMove]
  );

  // due to how we restructure this combobox, aria-labelledby is not needed.
  const { 'aria-labelledby': _, ...searchInputProps } = getInputProps(
    {
      onKeyDown: (e) => {
        if (e.key === 'Escape' && isOpen) {
          // closes the popper
          e.stopPropagation();
          e.currentTarget.blur();
        }
        if (e.key === 'Enter') {
          handleAddCustomItem();
        }
      },
      onFocus: onSearchInputFocus,
    },
    { suppressRefError: true }
  );

  // in order to customise behavior of the toggle button, we need to hijack the ref and tabIndex
  const {
    ref: _toggleButtonRef,
    tabIndex: _toggleButtonTabIndex,
    ...toggleButtonProps
  } = getToggleButtonProps({
    onBlur: onToggleBlur,
    onFocus: onToggleFocus,
    onMouseOver: onToggleMouseOver,
    onMouseLeave: onToggleMouseLeave,
    onKeyDown: (e) => {
      e.stopPropagation();
      if (e.key === 'Backspace') {
        setValue(null);
      }
    },
  });

  const renderSelectedItem = (item: Item): ReactNode => {
    if (customRenderSelectedItem) {
      return customRenderSelectedItem({ value: itemToStringValue(item), item });
    }

    return itemToStringLabel(item);
  };

  const attributeClassNames = clsx({
    [dropdownClassNames.open]: isOpen,
    [dropdownClassNames.disabled]: disabled,
    [dropdownClassNames.readOnly]: readOnly,
    [dropdownClassNames.error]: error,
    [dropdownClassNames.success]: success,
    [dropdownClassNames.sizeSmall]: size === 'small',
    [dropdownClassNames.sizeMedium]: size === 'medium',
    [dropdownClassNames.sizeLarge]: size === 'large',
  });

  return (
    <Box
      className={clsx(
        dropdownClassNames.root,
        dropdownClassNames.singleAutoComplete,
        className,
        attributeClassNames
      )}
      sx={sx}
      data-testid={testId}
    >
      <input
        className={dropdownClassNames.hiddenInput}
        type="hidden"
        value={value || ''}
        data-testid="hidden-value-input"
        readOnly={readOnly}
        disabled={disabled}
      />
      <MainContainer ref={anchorRef}>
        {prefixOutside && (
          <PrefixOutside data-testid="prefix-outside" inputHasLabel={!!label}>
            {prefixOutside}
          </PrefixOutside>
        )}

        {label && (
          <Label
            {...getLabelProps()}
            testId="label"
            className="downshift-label"
            disabled={disabled}
            size={autocompleteSizeToFormLabelSize(size)}
            hasOutsidePrefix={!!prefixOutside}
            hasOutsideSuffix={!!suffixOutside}
          >
            {label}
          </Label>
        )}

        {renderToggle ? (
          renderToggle({
            isOpen,
            tabIndex: 0,
            disabled,
            readOnly,
            error,
            success,
            toggleButtonProps: {
              ...toggleButtonProps,
              ref: toggleButtonRef,
            },
          })
        ) : (
          <ToggleButton
            type="button" // to prevent accidential form submissions
            data-testid="toggle-button"
            disabled={disabled || readOnly}
            aria-disabled={disabled || readOnly}
            aria-invalid={!!error}
            ref={toggleButtonRef}
            tabIndex={0}
            isOpen={isOpen}
            error={!!error}
            success={success}
            size={size}
            readOnly={readOnly}
            hasOutsidePrefix={!!prefixOutside}
            hasOutsideSuffix={!!suffixOutside}
            {...toggleButtonProps}
          >
            {prefix ? (
              <PrefixContainer data-testid="prefix" size={size}>
                {prefix}
              </PrefixContainer>
            ) : null}

            {selectedItem ? (
              <SelectedItemsContainer data-testid="selected-item" size={size}>
                {renderSelectedItem(selectedItem)}
              </SelectedItemsContainer>
            ) : (
              <PlaceholderContainer
                data-testid="placeholder"
                size={size}
                disabled={disabled}
              >
                {placeholder}
              </PlaceholderContainer>
            )}

            <CaretContainer isOpen={isOpen} size={size} disabled={disabled}>
              <Icons.CaretDown size={16} />
            </CaretContainer>
          </ToggleButton>
        )}

        {(!!error || !!hint) && (
          <HintMessage
            data-testid="hint-message"
            error={!!error}
            hasOutsidePrefix={!!prefixOutside}
            hasOutsideSuffix={!!suffixOutside}
            size={size}
          >
            {error || hint}
          </HintMessage>
        )}

        {suffixOutside && (
          <SuffixOutside data-testid="suffix-outside" inputHasLabel={!!label}>
            {suffixOutside}
          </SuffixOutside>
        )}
      </MainContainer>

      <Popper
        className={attributeClassNames}
        // due to how poppers are rendered, their testid must be unique, scoping with test drivers is not enough.
        data-testid={`${testId}-popper`}
        open={isOpen}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        sx={{ maxWidth: popoverSize?.maxWidth, width: popoverSize?.width }}
        modifiers={
          popoverSize?.width
            ? // if the popover size has a width, we need to remove the sameWidth modifier
              // because it will cause the popper to be the same width as the toggle button
              popperModifiers.filter((m) => m.name !== SAME_WIDTH_MODIFIER_NAME)
            : popperModifiers
        }
      >
        <DropdownContainer>
          <SearchInputContainer>
            <Icons.MagnifyingGlass size={15} />
            <SearchInput
              data-testid="search-input"
              placeholder={searchInputPlaceholder}
              autoFocus
              {...searchInputProps}
            />
          </SearchInputContainer>

          {filteredItems.length === 0 && (
            <NoItemsFound
              customRenderNoItemsFound={customRenderNoItemsFound}
              searchInputValue={searchInputValue}
              items={items}
            />
          )}

          {isItemUniqueValue && (
            <CustomItemHint data-testid="add-custom-item-hint">
              {addCustomItemHint}
            </CustomItemHint>
          )}

          {useVirtualization ? (
            <Virtuoso
              data-testid="option-item-container"
              data={enhancedFilteredItems}
              totalListHeightChanged={setVirtuosoHeight}
              style={{
                maxHeight: popoverMaxHeight,
                height: virtuosoHeight,
              }}
              itemContent={(index, item) =>
                renderMenuItemByType({
                  item,
                  index,
                  showCheckbox: false,
                  itemProps: getItemPropsWithMouseHandler({ item, index }),
                  customRenderMenuItem,
                  customRenderMenuItemGroupLabel,
                })
              }
              components={{
                Scroller: VirtuosoScroller,
              }}
              {...getMenuProps(
                { ref: menuRef as any },
                { suppressRefError: true }
              )}
            />
          ) : (
            <Scroller
              sx={{
                maxHeight: popoverMaxHeight,
                overflowY: 'auto',
              }}
            >
              <MenuList
                data-testid="option-item-container"
                {...getMenuProps(
                  { ref: menuRef as any },
                  { suppressRefError: true }
                )}
              >
                {enhancedFilteredItems.map((item, index) => (
                  <Fragment key={index}>
                    {renderMenuItemByType({
                      item,
                      index,
                      showCheckbox: false,
                      itemProps: getItemPropsWithMouseHandler({ item, index }),
                      customRenderMenuItem,
                      customRenderMenuItemGroupLabel,
                    })}
                  </Fragment>
                ))}
              </MenuList>
            </Scroller>
          )}
        </DropdownContainer>
      </Popper>
    </Box>
  );
};

Autocomplete.classNames = dropdownClassNames;
